import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../useApi';
import { ErrorNotice } from '../components/ErrorNotice';

// Who is on a free ride, and the three ways to change that.
//
// The console does NOT create coupons. Stripe's own form handles duration,
// redemption limits and product restrictions better than a reimplementation
// would, and stays current with their API for free. What it does instead is
// tell you exactly what to create over there, then do the fiddly half here:
// attaching it to the right subscription, and later removing it correctly.

interface Discount {
  discountId: string;
  couponId: string | null;
  couponName: string | null;
  percentOff: number | null;
  amountOff: number | null;
  duration: string | null;
  durationInMonths: number | null;
  endsAt: number | null;
}

interface Comp {
  subscriptionId: string;
  status: string;
  customerName: string | null;
  customerEmail: string | null;
  accountId: string | null;
  currency: string;
  amountWithoutDiscountText: string;
  discounts: Discount[];
}

interface CompsReport {
  comps: Comp[];
  truncated: boolean;
  stripeInstructions: Record<string, string>;
}

type Pending = { subscriptionId: string; kind: 'end' | 'apply' | 'refund' } | null;

function describe(d: Discount): string {
  const size = d.percentOff !== null ? `${d.percentOff}% off` : d.amountOff !== null ? `${(d.amountOff / 100).toFixed(2)} off` : 'discount';
  const span =
    d.duration === 'forever'
      ? 'forever'
      : d.duration === 'once'
        ? 'once'
        : d.durationInMonths
          ? `for ${d.durationInMonths} months`
          : (d.duration ?? '');
  return `${d.couponName ?? d.couponId ?? 'coupon'} — ${size}, ${span}`;
}

export function CompsPage() {
  const api = useApi();
  const [report, setReport] = useState<CompsReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [reason, setReason] = useState('');
  const [couponId, setCouponId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!api) return;
    try {
      setReport(await api.get<CompsReport>('/support/comps'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load comps.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  function start(subscriptionId: string, kind: NonNullable<Pending>['kind']) {
    setPending({ subscriptionId, kind });
    setReason('');
    setCouponId('');
    setError(null);
    setResult(null);
  }

  async function confirm() {
    if (!api || !pending || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { subscriptionId, kind } = pending;
      const path =
        kind === 'end'
          ? `/support/comps/${subscriptionId}/end`
          : kind === 'apply'
            ? `/support/comps/${subscriptionId}/apply`
            : `/support/comps/${subscriptionId}/refund`;

      const body = kind === 'apply' ? { reason, couponId } : { reason };
      const response = await api.post<{ note?: string; amountText?: string }>(path, body);

      setResult(
        [response.amountText ? `Refunded ${response.amountText}.` : null, response.note]
          .filter(Boolean)
          .join(' ')
      );
      setPending(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  if (!report) {
    return (
      <>
        <ErrorNotice message={error} />
        {!error && <p className="muted">Loading…</p>}
      </>
    );
  }

  return (
    <>
      <ErrorNotice message={error} />
      {result && <div className="notice success">{result}</div>}

      {/*
        The instructions exist because the console deliberately cannot create
        coupons. Without them you would be left guessing which settings to pick
        in Stripe, which is exactly how a "one month free" becomes a forever
        comp nobody notices for a year.
      */}
      <div className="card">
        <h2>Creating a coupon</h2>
        <p className="muted small">
          Coupons are created in Stripe, then applied here. Copy the coupon ID back
          into the box when you apply it.
        </p>
        <dl className="detail-grid">
          <dt>Comp forever</dt>
          <dd className="small">{report.stripeInstructions.compForever}</dd>
          <dt>One month free</dt>
          <dd className="small">{report.stripeInstructions.oneMonthFree}</dd>
          <dt>Half off, 12 months</dt>
          <dd className="small">{report.stripeInstructions.halfOffTwelveMonths}</dd>
        </dl>
      </div>

      <div className="card">
        <h2>Active discounts</h2>
        {report.comps.length === 0 ? (
          <p className="muted">Nobody is currently on a discount.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Discount</th>
                <th>Would pay</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {report.comps.map((comp) => (
                <tr key={comp.subscriptionId}>
                  <td>
                    {comp.customerName ?? '—'}
                    <br />
                    <span className="muted small">{comp.customerEmail ?? comp.subscriptionId}</span>
                  </td>
                  <td className="small">
                    {comp.discounts.map((d) => (
                      <div key={d.discountId}>{describe(d)}</div>
                    ))}
                  </td>
                  {/* The column that makes a comp visible as a number. */}
                  <td className="mono">{comp.amountWithoutDiscountText}</td>
                  <td>
                    <div className="toolbar">
                      <button type="button" onClick={() => start(comp.subscriptionId, 'apply')}>
                        Apply coupon
                      </button>
                      <button type="button" onClick={() => start(comp.subscriptionId, 'end')}>
                        End comp
                      </button>
                      <button type="button" onClick={() => start(comp.subscriptionId, 'refund')}>
                        Refund last payment
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {report.truncated && (
          <p className="muted small">
            Showing the most recent subscriptions only — older ones are not listed.
          </p>
        )}
      </div>

      {pending && (
        <div className="card">
          <h2>
            {pending.kind === 'end'
              ? 'End this comp'
              : pending.kind === 'apply'
                ? 'Apply a coupon'
                : 'Refund the last payment'}
          </h2>

          {/*
            Each action says what it does AND what it does not do. The gap
            between "credit their next invoice" and "give them their money
            back" is the one people get wrong under pressure.
          */}
          <div className="notice warn">
            {pending.kind === 'end' && (
              <>
                <strong>They start paying from their next invoice.</strong> Stripe does
                not prorate this, so there is no mid-cycle charge. This does not delete
                the coupon — do that separately in Stripe if you want to stop anyone
                else redeeming it.
              </>
            )}
            {pending.kind === 'apply' && (
              <>
                <strong>This affects their next invoice, not the last one.</strong> If
                they are annoyed about a month they have already paid for, a coupon does
                not give that money back — use “Refund last payment” instead.
              </>
            )}
            {pending.kind === 'refund' && (
              <>
                <strong>This moves real money.</strong> It refunds the most recent paid
                invoice on this subscription to the card that paid it, and typically
                takes 5–10 business days to appear. It does not cancel or change the
                subscription.
              </>
            )}
          </div>

          {pending.kind === 'apply' && (
            <label>
              <span>Coupon ID from Stripe</span>
              <input
                value={couponId}
                onChange={(e) => setCouponId(e.target.value)}
                className="mono"
                placeholder="6CneyDTP"
                maxLength={100}
              />
            </label>
          )}

          <label>
            <span>Reason</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you doing this?"
              maxLength={500}
            />
            <small className="muted">
              Recorded in the audit log. Write it for whoever reads this in six months,
              which may well be you.
            </small>
          </label>

          <div className="form-actions">
            <button type="button" onClick={() => setPending(null)} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void confirm()}
              disabled={busy || !reason.trim() || (pending.kind === 'apply' && !couponId.trim())}
            >
              {busy ? 'Working…' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
